import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const CONTINUOUS_HEART_RATE_TYPE = 'P360_HR_5m';
const FIVE_MINUTES = 5 * 60 * 1000;
const PERIOD_DAYS = { '7d': 7, '30d': 30, '90d': 90 };

export const getContinuousHeartRateRange = ({
    period,
    patientCreated,
    zone,
    now = Date.now()
}) => {
    const today = dayjs(now).tz(zone);
    const created = patientCreated ? dayjs(patientCreated).tz(zone) : null;
    const start = PERIOD_DAYS[period]
        ? today.subtract(PERIOD_DAYS[period] - 1, 'day')
        : created?.isValid() && !created.isAfter(today)
          ? created
          : today.subtract(1, 'year');
    return {
        startTimestamp: dayjs.tz(start.format('YYYY-MM-DD'), zone).toISOString(),
        endTimestamp: dayjs
            .tz(today.add(1, 'day').format('YYYY-MM-DD'), zone)
            .toISOString()
    };
};

export const normalizeContinuousHeartRate = (table, { startTimestamp, endTimestamp }) => {
    const start = Date.parse(startTimestamp);
    const end = Date.parse(endTimestamp);
    if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end) return [];
    const points = new Map();
    const columns = Array.isArray(table?.columns) ? table.columns : [];
    const timeIndex = columns.indexOf('ts');
    const hrIndex = columns.indexOf('hr');
    for (const row of Array.isArray(table?.data) ? table.data : []) {
        if (
            timeIndex < 0 ||
            hrIndex < 0 ||
            !Array.isArray(row) ||
            row.length !== columns.length
        )
            continue;
        const timestamp =
            typeof row[timeIndex] === 'string' ? Date.parse(row[timeIndex]) : NaN;
        const rawHr = row[hrIndex];
        if (
            (typeof rawHr !== 'number' && typeof rawHr !== 'string') ||
            String(rawHr).trim() === ''
        )
            continue;
        const hr = Number(rawHr);
        if (
            !Number.isFinite(timestamp) ||
            timestamp < start ||
            timestamp >= end ||
            !Number.isFinite(hr) ||
            hr <= 0
        )
            continue;
        // The compact response's ts column is the corrected measurement time.
        points.set(timestamp, { timestamp, hr });
    }
    const sorted = [...points.values()].sort((a, b) => a.timestamp - b.timestamp);
    const chart = [];
    for (const point of sorted) {
        const previous = chart.at(-1);
        // Allow timestamp jitter, but don't draw a line across missing five-minute windows.
        if (previous && point.timestamp - previous.timestamp > FIVE_MINUTES * 1.5) {
            chart.push({ timestamp: previous.timestamp + FIVE_MINUTES, hr: null });
        }
        chart.push(point);
    }
    return chart;
};
